import React, { useState, useCallback, useRef, useEffect } from 'react';
import { PaperClipIcon, XMarkIcon, DocumentIcon } from '@heroicons/react/24/outline'; // Import icons
import { useAnalytics } from '@contexts/analytics';

import DragAndDropFileUpload from '@components/DragAndDropFileUpload';

// Import the utility function to get page count
import { getPdfPageCount } from '@helpers/pdf';

// --- Updated FileUploadStep Component ---
// Now simplified, only manages upload logic and display of the selected file
const FileUploadStep = ({ onFileSelected, onFileRemove, file }) => {
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [displayedFile, setDisplayedFile] = useState(file || null);
  const [errorMsg, setErrorMsg] = useState('');
  const { track } = useAnalytics();

  // Constants for validation remain in the component
  const MAX_FILE_SIZE_MB = 10;
  const MAX_PAGE_COUNT = 30; // Example: Set your desired max page count

  // Effect to sync internal displayed file with the file prop from parent
  useEffect(() => {
    setDisplayedFile(file || null);
    if (file) {
      setErrorMsg('');
      if (uploadStatus === 'error') {
        setUploadStatus('idle');
      }
    }
  }, [file, uploadStatus]);

  // This function is now called by DragAndDropArea when a file is ready
  const handleFileProcessing = useCallback(async (fileObject) => {
    if (!fileObject) return;

    setUploadStatus('uploading');
    setDisplayedFile(null);
    setErrorMsg('');

    // Calculate file size early for tracking
    const fileSizeInMB = fileObject.size / (1024 * 1024);

    try {
      // Get PDF page count early for tracking
      let numPages;
      try {
        numPages = await getPdfPageCount(fileObject);
      } catch (pdfError) {
        // Ignore page count errors for tracking purposes
      }

      // Track file info before any validation occurs
      track('Lesson Generation File Attempt', {
        fileName: fileObject.name,
        fileSize: fileSizeInMB.toFixed(2),
        pageCount: numPages || 'unknown',
        fileType: fileObject.type,
        fileSizeLimit: MAX_FILE_SIZE_MB,
        pageCountLimit: MAX_PAGE_COUNT
      });

      await new Promise(resolve => setTimeout(resolve, 500));

      // 1. Check file size
      if (fileSizeInMB > MAX_FILE_SIZE_MB) {
        setErrorMsg(`File size must be less than ${MAX_FILE_SIZE_MB}MB`);
        setUploadStatus('error');
        return;
      }

      // 2. Validate page count
      if (numPages > MAX_PAGE_COUNT) {
        setErrorMsg(`PDF must have ${MAX_PAGE_COUNT} pages or fewer.`);
        setUploadStatus('error');
        return;
      }

      onFileSelected(fileObject);
      setUploadStatus('idle'); // Set status to idle after successfully notifying parent

    } catch (err) {
      if (!errorMsg) {
        setErrorMsg(`Processing failed for ${fileObject.name}. Please try again.`);
      }
      setUploadStatus('error');
      setDisplayedFile(null);
      if (onFileRemove) {
        onFileRemove(); // Ensure parent state is cleared on unexpected failure
      }
    }
  }, [onFileSelected, onFileRemove, errorMsg]); // Removed uploadStatus, file from deps - simplified

  // Renamed for clarity, still handles removal triggered by the X button
  const handleRemoveUploadedFile = useCallback(() => {
    const fileToRemove = displayedFile || file;
    if (!fileToRemove) return;

    setDisplayedFile(null);
    setErrorMsg('');
    setUploadStatus('idle');

    if (onFileRemove) {
      onFileRemove();
    }
  }, [displayedFile, file, onFileRemove]);

  // File to display is now just the internal state synced from props
  const currentFileToDisplay = displayedFile;

  return (
    <div className="flex-1 flex flex-col">
      {/* Heading Section */}
      <div className="space-y-2 mb-4">
        <h2 className="text-2xl font-medium mb-1">Upload your content</h2>
        <p className="text-lg text-gray-500 font-light">
          Paste any notes, textbook excerpts, or your own slides — Our AI will structure it into an engaging lesson.
        </p>
      </div>

      {/* Drag and Drop Area - Pass fewer props now */}
      <div className="flex items-start justify-center">
        <DragAndDropFileUpload
          uploadStatus={uploadStatus}
          errorMsg={errorMsg}
          onFileReadyToUpload={handleFileProcessing} // Pass the processing handler
          options={{
            accepts: ['.pdf'],
            maxFileSizeMB: 10,
            acceptsDocCount: 1,
            acceptsOverrideText: "Accepts one PDF, up to 10MB"
          }}
        />
      </div>

      {/* Display Area for Successfully Uploaded File */}
      {currentFileToDisplay && uploadStatus !== 'uploading' && uploadStatus !== 'error' && (
        <div className="w-full pt-4">
          <div className="flex items-center justify-between bg-background border border-accent p-3 rounded-lg">
            <div className="flex items-center gap-3 truncate">
              <div className="flex-shrink-0 bg-accent p-3 rounded-md">
                <DocumentIcon className="h-5 w-5 text-primary" />
              </div>
              <div className="flex flex-col truncate">
                <span className="text-md font-medium text-primary truncate" title={currentFileToDisplay.name}>
                  {currentFileToDisplay.name}
                </span>
                <span className="text-sm text-primary/60 font-light">
                  {(currentFileToDisplay.size / (1024 * 1024)).toFixed(1)}MB
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={handleRemoveUploadedFile}
              className="ml-4 flex-shrink-0 p-1 text-primary hover:text-primary/60 focus:outline-none"
              title="Remove file"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default FileUploadStep; 
